<template>
  <SideMenu
    ref="sideMenu"
    :heading="$lang('account', 'my_pages')"
    :side="'right'"
    @on-side-menu-close="emit('close')"
  >
    <div class="bg-main h-full flex flex-col p-24">
      <!-- Loading State -->
      <div v-if="isCheckingLogin" class="flex flex-col items-center justify-center py-48">
        <fa
          class="text-muted text-2xl mb-16"
          :icon="['fal', 'spinner']"
          :spin="true"
        />
        <p class="text-muted text-sm">{{ $lang('account', 'loading') }}</p>
      </div>
      
      <div v-else-if="isLoggedIn" class="flex flex-col space-y-16">
        <div class="mb-16 pb-16 border-b border-default">
          <div v-if="customerName" class="text-heading-small mb-8">
            {{ customerName }}
          </div>
          <div class="text-sm text-muted">
            {{ customerEmail }}
          </div>
        </div>
        
        <nuxt-link 
          :to="`/${locale}/account/orders`" 
          class="flex items-center py-12 px-16 hover:bg-light rounded-regular transition-colors"
          @click="closeMenu()"
        >
          <img src="~/assets/icons/shopping-bag.png" class="h-20 w-20 mr-12" width="20" height="20" :alt="$lang('account', 'my_orders')">
          <span class="text-standard">{{ $lang('account', 'my_orders') }}</span>
        </nuxt-link>
        
        <nuxt-link 
          :to="`/${locale}/account/wishlists`" 
          class="flex items-center py-12 px-16 hover:bg-light rounded-regular transition-colors"
          @click="closeMenu()"
        >
          <fa 
            :icon="['fa' + (wishlistStore.itemCount > 0 ? 's' : 'l'), 'heart']" 
            class="h-20 w-20 mr-12 text-muted wishlist-heart-icon" 
            :class="{ 'text-red-full': wishlistStore.itemCount > 0, 'blink-heart': cartStore.highlightWishlist }"
          />
          <span class="text-standard">{{ $lang('wishlist', 'my_wishlist') }}</span>
        </nuxt-link>
        
        <nuxt-link 
          :to="`/${locale}/account/profile`" 
          class="flex items-center py-12 px-16 hover:bg-light rounded-regular transition-colors"
          @click="closeMenu()"
        >
          <svg class="h-20 w-20 mr-12 text-muted" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
            <circle cx="12" cy="7" r="4"></circle>
          </svg>
          <span class="text-standard">{{ $lang('account', 'my_profile') }}</span>
        </nuxt-link>
        
        <div class="border-t border-default my-16" />
        
        <button
          class="btn btn--md btn--secondary w-full"
          @click="handleLogout"
        >
          {{ $lang('account', 'log_out') }}
        </button>
      </div>
      
      <div v-else class="flex flex-col space-y-16">
        <!-- Info Message -->
        <div v-if="infoMessage" class="bg-blue-25 border border-blue-50 text-blue-full px-16 py-12 rounded-regular text-sm">
          {{ infoMessage }}
        </div>
        
        <div class="mb-16">
          <h3 class="text-heading-small mb-16">{{ $lang('account', 'login') }}</h3>
          <form @submit.prevent="handleLogin" class="space-y-12">
            <div>
              <label class="block text-sm mb-8 text-standard font-medium">{{ $lang('account', 'email') }}</label>
              <TextInput 
                v-model="loginEmail"
                class="mb-12"
                :placeholder="$lang('account', 'email_placeholder')"
                type="email" 
                required
              />
            </div>
            <div>
              <label class="block text-sm mb-8 text-standard font-medium">{{ $lang('account', 'password') }}</label>
              <TextInput 
                v-model="loginPassword" 
                type="password" 
                required 
                placeholder="********"
              />
            </div>
            <div v-if="loginSuccess" class="text-success text-sm bg-green-25 px-12 py-8 rounded-regular">
              {{ loginSuccess }}
            </div>
            <div v-if="loginError" class="text-warning text-sm bg-red-25 px-12 py-8 rounded-regular">
              {{ loginError }}
            </div>
            <button 
              type="submit" 
              class="btn btn--primary btn--md w-full"
              :disabled="loginLoading"
            >
              <span v-if="!loginLoading">{{ $lang('account', 'login_button') }}</span>
              <fa
                v-else
                class="text-sm"
                :icon="['fal', 'spinner']"
                :spin="true"
              />
            </button>
            <button 
              type="button"
              class="w-full text-sm text-muted hover:text-primary transition-colors"
              @click="showForgotPassword = true"
            >
              {{ $lang('account', 'forgot_password') }}
            </button>
          </form>
        </div>
        
        <div class="border-t border-default pt-16">
          <h3 class="text-heading-small mb-16">{{ $lang('account', 'new_customer') }}</h3>
          <button
            class="btn btn--md btn--transparent w-full"
            @click="showRegister = true"
          >
            {{ $lang('account', 'create_account') }}
          </button>
        </div>
      </div>
    </div>
  </SideMenu>

  <!-- Forgot Password Modal -->
  <ForgotPasswordModal 
    v-model="showForgotPassword" 
    :initial-email="loginEmail"
  />

      <!-- Register Form -->
      <Teleport to="body">
        <div v-if="showRegister" class="fixed inset-0 bg-black-transparent flex items-center justify-center" style="z-index: 10001" @click.self="showRegister = false">
          <div class="bg-white p-32 rounded-regular shadow-lg max-h-screen overflow-auto w-full mx-24 max-w-600">
            <h3 class="text-heading-small mb-24">{{ $lang('account', 'create_account') }}</h3>
            <form @submit.prevent="handleRegister" class="space-y-16">
              <div>
                <label class="block text-sm mb-8 text-standard font-medium">{{ $lang('account', 'first_name') }}</label>
                <TextInput 
                  v-model="registerFirstName" 
                  required 
                />
              </div>
              <div>
                <label class="block text-sm mb-8 text-standard font-medium">{{ $lang('account', 'last_name') }}</label>
                <TextInput 
                  v-model="registerLastName" 
                  required 
                />
              </div>
              <div>
                <label class="block text-sm mb-8 text-standard font-medium">{{ $lang('account', 'email') }}</label>
                <TextInput 
                  v-model="registerEmail" 
                  type="email" 
                  required 
                  placeholder=""
                />
              </div>
              <div class="text-sm text-gray-600 bg-blue-25 px-12 py-8 rounded-regular">
                {{ $lang('account', 'email_verification_message') }}
              </div>
              <div v-if="registerError" class="text-warning text-sm bg-red-25 px-12 py-8 rounded-regular">
                {{ registerError }}
              </div>
              <div class="flex gap-12 pt-8">
                <button 
                  type="button"
                  class="btn btn--md btn--secondary flex-1"
                  @click="showRegister = false"
                >
                  {{ $lang('account', 'cancel') }}
                </button>
                <button 
                  type="submit" 
                  class="btn btn--md btn--primary flex-1"
                  :disabled="registerLoading"
                >
                  {{ registerLoading ? $lang('account', 'creating') : $lang('account', 'create_account_button') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </Teleport>

      <!-- Reset Password Form -->
      <Teleport to="body">
        <div v-if="showResetPassword" class="fixed inset-0 bg-black-transparent flex items-center justify-center" style="z-index: 10001" @click.self="showResetPassword = false">
          <div class="bg-white p-32 rounded-regular shadow-lg w-full mx-24 max-w-600">
            <h3 class="text-heading-small mb-24">{{ $lang('account', 'reset_password_title') }}</h3>
            <p v-if="resetLinkValid" class="text-sm text-muted mb-24">{{ $lang('account', 'reset_password_description') }}</p>
            <form v-if="resetLinkValid" @submit.prevent="handleResetPassword" class="space-y-16">
              <div>
                <label class="block text-sm mb-8 text-standard font-medium">{{ $lang('account', 'new_password') }}</label>
                <TextInput 
                  v-model="resetPassword" 
                  type="password"
                  :placeholder="$lang('account', 'new_password')"
                  required 
                />
              </div>
              <div>
                <label class="block text-sm mb-8 text-standard font-medium">{{ $lang('account', 'confirm_new_password') }}</label>
                <TextInput 
                  v-model="resetConfirmPassword" 
                  type="password"
                  :placeholder="$lang('account', 'confirm_new_password')"
                  required 
                />
              </div>
              <div v-if="resetError" class="text-warning text-sm bg-red-25 px-12 py-8 rounded-regular">
                {{ resetError }}
              </div>
              <div v-if="resetSuccess" class="text-success text-sm bg-green-25 px-12 py-8 rounded-regular">
                {{ resetSuccess }}
              </div>
              <div class="flex gap-12 pt-8">
                <button 
                  type="submit" 
                  class="btn btn--md btn--primary w-full"
                  :disabled="resetLoading"
                >
                  {{ resetLoading ? $lang('account', 'resetting') : $lang('account', 'reset_password_button') }}
                </button>
              </div>
            </form>
            <div v-else class="space-y-16">
              <p role="alert" class="text-warning text-sm bg-red-25 px-12 py-8 rounded-regular">
                {{ $lang('account', 'invalid_reset_link') }}
              </p>
              <button
                type="button"
                class="btn btn--md btn--primary w-full"
                @click="requestNewResetLink"
              >
                {{ $lang('account', 'send_reset_link') }}
              </button>
            </div>
          </div>
        </div>
      </Teleport>
</template>

<script setup lang="ts">
import SideMenu from '~/components/navigation/SideMenu.vue';
import TextInput from '~/components/forms/TextInput.vue';
import ForgotPasswordModal from '~/components/account/ForgotPasswordModal.vue';
import { useCartStore } from '~/store/cart';
import { useWishlistStore } from '~/store/wishlist';
import { useConfigStore } from '~/store/config';

const emit = defineEmits(['close']);
const { $lang } = useNuxtApp();

const router = useRouter();
const route = useRoute();
const cartStore = useCartStore();
const wishlistStore = useWishlistStore();
const config = useConfigStore();
const sideMenu = ref<typeof SideMenu | null>(null);
const isLoggedIn = ref(false);
const customerEmail = ref('');
const infoMessage = computed(() => cartStore.accountMenuMessage);

// Get locale from route for proper URL construction
const locale = computed(() => {
  const pathSegments = route.path.split('/').filter(Boolean);
  return pathSegments[0] || 'se';
});

// Login
const loginEmail = ref('');
const loginPassword = ref('');
const loginError = ref('');
const loginSuccess = ref('');
const loginLoading = ref(false);

// Forgot password
const showForgotPassword = ref(false);

// Register
const showRegister = ref(false);
const registerFirstName = ref('');
const registerLastName = ref('');
const registerEmail = ref('');
const registerError = ref('');
const registerLoading = ref(false);
const customerName = ref('');
const isCheckingLogin = ref(true);

// Reset password (from email link)
const showResetPassword = ref(false);
const resetPassword = ref('');
const resetConfirmPassword = ref('');
const resetError = ref('');
const resetSuccess = ref('');
const resetLoading = ref(false);
const resetLinkValid = ref(true);
const resetCustomerId = ref('');
const resetHash = ref('');

onMounted(async () => {
  // Check for password reset params in URL
  if (route.query.id && route.query.i) {
    resetCustomerId.value = route.query.id as string;
    resetHash.value = route.query.i as string;
    showResetPassword.value = true;
    resetLinkValid.value = true;
  }
  
  if (sideMenu.value) {
    sideMenu.value.openOverlay();
  }
  await checkLoginStatus();
  isCheckingLogin.value = false;
  
  // Check if wishlist celebration is active when menu opens
  if (cartStore.highlightWishlist) {
    closeTimeout = setTimeout(() => {
      closeMenu();
      closeTimeout = null;
    }, 2500);
  }
});

onUnmounted(() => {
  // Clear message when menu is closed
  cartStore.accountMenuMessage = '';
  // Clear any pending timeout
  if (closeTimeout) {
    clearTimeout(closeTimeout);
  }
});

// Watch for wishlist highlight animation ending to auto-close menu
let closeTimeout: NodeJS.Timeout | null = null;
watch(() => cartStore.highlightWishlist, (isHighlighted) => {
  if (isHighlighted) {
    // Clear any existing timeout
    if (closeTimeout) {
      clearTimeout(closeTimeout);
    }
    // Set timeout to close menu after animation completes
    closeTimeout = setTimeout(() => {
      closeMenu();
      closeTimeout = null;
    }, 2500); // Match the animation duration (2s animation + 0.5s buffer)
  }
  // Don't cancel the timeout when highlight turns off - let it complete
});

function closeMenu() {
  sideMenu.value?.closeOverlay();
}

async function checkLoginStatus() {
  try {
    const res = await $fetch('/api/centra/me');
    if (res && res.email) {
      isLoggedIn.value = true;
      customerEmail.value = res.email;
      customerName.value = res.firstName && res.lastName 
        ? `${res.firstName} ${res.lastName}` 
        : res.firstName || res.lastName || '';
    }
  } catch (err) {
    isLoggedIn.value = false;
  }
}

async function handleLogin() {
  loginError.value = '';
  loginSuccess.value = '';
  loginLoading.value = true;
  
  try {
    const res = await $fetch('/api/centra/login', {
      method: 'POST',
      body: {
        email: loginEmail.value,
        password: loginPassword.value,
      },
    });
    
    if (res.success) {
      // Refresh cart data to get updated user information
      await cartStore.initialCart();
      
      isLoggedIn.value = true;
      customerEmail.value = loginEmail.value;
      loginEmail.value = '';
      loginPassword.value = '';
      
      // If there was a message (e.g., wishlist prompt), close the menu after login
      if (cartStore.accountMenuMessage) {
        cartStore.accountMenuMessage = '';
        setTimeout(() => {
          closeMenu();
        }, 500); // Small delay to show logged in state
      }
    }
  } catch (err: any) {
    loginError.value = err.data?.message || $lang('account', 'login_failed');
  } finally {
    loginLoading.value = false;
  }
}

async function handleLogout() {
  try {
    // Call logout API to clear session on server
    await $fetch('/api/centra/logout', { method: 'POST' });
  } catch (err) {
    console.error('Logout error:', err);
  }
  
  // Clear local state
  isLoggedIn.value = false;
  customerEmail.value = '';
  customerName.value = '';
  closeMenu();
  
  // Use window.location.href for full page reload with correct locale
  const redirectUrl = `/${locale.value}/`;
  window.location.href = redirectUrl;
}

async function handleRegister() {
  registerError.value = '';
  registerLoading.value = true;

  try {
    const res = await $fetch('/api/centra/register',{
      method: 'POST',
      body: {
        email: registerEmail.value,
        firstName: registerFirstName.value,
        lastName: registerLastName.value,
        locale: locale.value,
        country: config.market.country,
      },
    });

    if (res.success) {
      // Clear form
      registerFirstName.value = '';
      registerLastName.value = '';
      registerEmail.value = '';
      showRegister.value = false;
      
      // Show success message on login form - always use translation
      loginSuccess.value = $lang('account', 'account_created_verify_email');
    } else {
      console.error('[AccountMenu] Response missing success flag:', res);
      registerError.value = $lang('account', 'registration_failed');
    }
  } catch (err: any) {
    console.error('[AccountMenu] Registration error:', err);
    
    // Check for specific error codes from server for better translations
    const errorCode = err.data?.data?.code;
    const errorMessage = err.data?.message;
    
    // Use translated error messages based on error code
    if (errorCode === 'invalid_email_format') {
      registerError.value = $lang('forms', 'invalid_email'); // Use existing translation
    } else if (errorCode === 'email_already_exists') {
      registerError.value = $lang('account', 'email_already_exists');
    } else if (errorCode === 'activation_email_failed') {
      registerError.value = $lang('account', 'failed_send_reset_email');
    } else {
      // Fall back to server message or generic error
      registerError.value = errorMessage || $lang('account', 'registration_failed');
    }
  } finally {
    registerLoading.value = false;
  }
}

async function handleResetPassword() {
  resetError.value = '';
  resetSuccess.value = '';
  if (!resetLinkValid.value || resetLoading.value) return;
  
  if (resetPassword.value !== resetConfirmPassword.value) {
    resetError.value = $lang('account', 'passwords_do_not_match');
    return;
  }
  
  if (resetPassword.value.length < 8) {
    resetError.value = $lang('account', 'password_min_length');
    return;
  }
  
  resetLoading.value = true;
  
  try {
    const res = await $fetch('/api/centra/reset-password', {
      method: 'POST',
      body: {
        id: resetCustomerId.value,
        i: resetHash.value,
        password: resetPassword.value,
      },
    });
    
    if (res.changed) {
      resetSuccess.value = res.loggedIn 
        ? $lang('account', 'password_reset_success_logged_in')
        : $lang('account', 'password_reset_success');
      
      // Clear query params from URL
      router.replace({ query: {} });
      
      setTimeout(() => {
        showResetPassword.value = false;
        if (res.loggedIn) {
          // Refresh login status and redirect
          isLoggedIn.value = true;
          router.push(`/${locale.value}/account/orders`);
        }
      }, 1500);
    }
  } catch (err: unknown) {
    const resetFailure = err as {
      data?: { data?: { code?: string }; statusCode?: number };
      statusCode?: number;
      response?: { status?: number };
    };
    const code = resetFailure?.data?.data?.code;
    const status = resetFailure?.response?.status ?? resetFailure?.statusCode ?? resetFailure?.data?.statusCode;
    if (code === 'invalid_reset_link' || (!code && status === 406)) {
      resetLinkValid.value = false;
      resetPassword.value = '';
      resetConfirmPassword.value = '';
      resetCustomerId.value = '';
      resetHash.value = '';
      const query = { ...route.query };
      delete query.id;
      delete query.i;
      await router.replace({ query });
    } else {
      resetError.value = $lang('account', 'failed_reset_password');
    }
  } finally {
    resetLoading.value = false;
  }
}

function requestNewResetLink() {
  showResetPassword.value = false;
  showForgotPassword.value = true;
}
</script>

<style scoped>
@keyframes blinkHeart {
  0%, 100% { 
    opacity: 1; 
    transform: scale(1);
  }
  25% { 
    opacity: 0.4; 
    transform: scale(1.2);
  }
  50% { 
    opacity: 1; 
    transform: scale(1);
  }
  75% { 
    opacity: 0.4; 
    transform: scale(1.2);
  }
}

.blink-heart {
  animation: blinkHeart 1s ease-in-out 2;
}
</style>
